import type { ModuleManifest, StatusKind } from '@sdk';

export type ShellHandles = {
  root: HTMLElement;
  chartEl: HTMLElement;
  panelEl: HTMLElement;
  moduleListEl: HTMLElement;
  statusEl: HTMLElement;
  healthEl: HTMLElement;
  setStatus: (text: string, kind?: StatusKind) => void;
  mountPanel: (el: HTMLElement) => void;
  renderModules: (
    modules: ModuleManifest[],
    activeId: string,
    onPick: (id: string) => void,
  ) => void;
  setHealth: (payload: Record<string, unknown> | null) => void;
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') node.className = v;
    else node.setAttribute(k, v);
  }
  for (const child of children) {
    node.append(child);
  }
  return node;
}

export function createShell(app: HTMLElement): ShellHandles {
  app.replaceChildren();

  const healthEl = el('span', { class: 'health-pill', title: 'Aggregator reachability' }, ['checking']);
  const statusEl = el('span', { class: 'status idle' }, ['idle']);

  const header = el('header', { class: 'topbar' }, [
    el('div', { class: 'brand' }, [
      el('span', { class: 'mark' }, ['FL']),
      el('div', {}, [
        el('h1', {}, ['FinLab']),
        el('p', { class: 'sub' }, ['strategy / paper / backtest laboratory']),
      ]),
    ]),
    el('div', { class: 'topbar-right' }, [healthEl, statusEl]),
  ]);

  const moduleListEl = el('nav', { class: 'module-list', 'aria-label': 'Modules' });
  const chartEl = el('div', { class: 'chart-host', id: 'chart' });
  const panelEl = el('section', { class: 'module-panel' });

  const aside = el('aside', { class: 'rail' }, [
    el('p', { class: 'rail-label' }, ['Modules']),
    moduleListEl,
    el('p', { class: 'rail-foot' }, [
      'Delete a folder under ',
      el('code', {}, ['modules/']),
      ' and it disappears on next boot.',
    ]),
  ]);

  const stage = el('div', { class: 'stage' }, [chartEl, panelEl]);
  const body = el('div', { class: 'body' }, [aside, stage]);
  app.append(header, body);

  function setStatus(text: string, kind: StatusKind = 'idle') {
    statusEl.className = `status ${kind}`;
    statusEl.textContent = text;
  }

  function mountPanel(node: HTMLElement) {
    panelEl.replaceChildren(node);
  }

  function renderModules(
    modules: ModuleManifest[],
    activeId: string,
    onPick: (id: string) => void,
  ) {
    moduleListEl.replaceChildren();
    const items: { id: string; title: string; subtitle?: string }[] = [
      { id: 'core', title: 'Overview', subtitle: 'Price chart · core' },
      ...modules.map((m) => ({ id: m.id, title: m.title, subtitle: m.subtitle })),
    ];
    for (const item of items) {
      const btn = el('button', {
        class: `mod-btn${item.id === activeId ? ' active' : ''}`,
        type: 'button',
      });
      btn.append(
        el('span', { class: 'mod-title' }, [item.title]),
        el('span', { class: 'mod-sub' }, [item.subtitle || item.id]),
      );
      btn.addEventListener('click', () => onPick(item.id));
      moduleListEl.append(btn);
    }
  }

  function setHealth(payload: Record<string, unknown> | null) {
    if (!payload) {
      healthEl.textContent = 'health ?';
      healthEl.dataset.kind = 'idle';
      return;
    }
    const reachable = payload.aggregator_reachable;
    const configured = payload.aggregator_configured;
    if (reachable === true) {
      healthEl.textContent = 'aggregator ok';
      healthEl.dataset.kind = 'ok';
    } else if (configured) {
      healthEl.textContent = 'aggregator down';
      healthEl.dataset.kind = 'err';
    } else {
      healthEl.textContent = 'no api key';
      healthEl.dataset.kind = 'warn';
    }
    healthEl.title = JSON.stringify(
      {
        aggregator_url: payload.aggregator_url,
        aggregator_reachable: payload.aggregator_reachable,
        aggregator_configured: payload.aggregator_configured,
      },
      null,
      0,
    );
  }

  return {
    root: app,
    chartEl,
    panelEl,
    moduleListEl,
    statusEl,
    healthEl,
    setStatus,
    mountPanel,
    renderModules,
    setHealth,
  };
}
